import React, { useState } from 'react'
import axios from 'axios';
import { MdOutlineAccessTime, MdStorefront } from "react-icons/md";
import benefitFallback from '../../assets/benfit.png'
import { convertToIsraelTime } from '../../util/formatsForDate';
import BenefitConfirmPopup from './BenefitConfirmPopup';
import BenefitResultPopup from './BenefitResultPopup';
import PremiumPopup from './PremiumPopup';
import SuccessAnimation from './SuccessAnimation';
import PaymentFailure from './PaymentFailure';
import { useUser } from './UserProvider';

const DEFAULT_ERROR_MESSAGE = "אירעה שגיאה במימוש ההטבה, אנא נסה שוב מאוחר יותר";

// imageUrl יכול להגיע כמפתח ב-S3 או כקישור מלא
const resolveImageUrl = (imageUrl) => {
  if (!imageUrl) return "";
  if (/^https?:\/\//i.test(imageUrl)) return imageUrl;
  return `https://archivebureau.s3.eu-west-3.amazonaws.com/${imageUrl}`;
}

// קישור בלי סכימה (למשל nike.co.il) ייפתח כנתיב יחסי באפליקציה, לכן מוסיפים https://
const toAbsoluteUrl = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

// מציגים רק את תאריך הסיום, למשל: בתוקף עד 15/10/2026
const getValidityText = (endDate) => (endDate ? `בתוקף עד ${convertToIsraelTime(endDate)}` : "");

// ניסוח חלון האישור לפי סוג יצירת הקשר של ההטבה
const getConfirmContent = (benefit) => {
  const provider = benefit?.providerName || "נותן ההטבה";
  if (benefit?.contactType === 'LINK') {
    return {
      title: "מעבר לאתר נותן ההטבה",
      message: `בלחיצה על אישור תועבר/י לאתר של ${provider} למימוש ההטבה.`,
    };
  }
  return {
    title: "מימוש ההטבה",
    message: `בלחיצה על אישור פרטי הקשר שלך יישלחו ל${provider} לצורך יצירת קשר איתך למימוש ההטבה.`,
  };
}

const BenefitCard = ({ benefit }) => {
  const { userDetails, setUserDetails } = useUser();
  // "" | "confirm" | "success" | "error" | "PremiumPopup" | "duesSuccess" | "duesError"
  const [step, setStep] = useState("");
  const [loading, setLoading] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  // הודעה שחוזרת מתהליך תשלום דמי החבר (כמו errMess במארזים)
  const [errMess, setErrMess] = useState("");
  const validityText = getValidityText(benefit?.endDate);
  const confirmContent = getConfirmContent(benefit);

  const isLink = benefit?.contactType === 'LINK';
  const isEmail = benefit?.contactType === 'EMAIL';
  const canRedeem = (isLink || isEmail) && !!benefit?.id;

  const handleCardClick = () => {
    if (!canRedeem) return;
    // כמו במארזים: חבר ללא דמי חבר פעילים מקבל את חלון ההצטרפות לדמי חבר במקום מימוש ההטבה
    if (benefit?.allow === false) {
      setStep("PremiumPopup");
      return;
    }
    setStep("confirm");
  }

  const handleConfirm = async () => {
    // מניעת לחיצה כפולה בזמן שהקריאה לשרת רצה
    if (loading) return;
    setLoading(true);

    // דפדפנים בנייד חוסמים window.open שמתבצע אחרי await, לכן פותחים טאב ריק כבר בלחיצה
    // ומכוונים אותו לכתובת רק אחרי שהשרת מחזיר תשובה
    const pendingWindow = isLink ? window.open('', '_blank') : null;

    try {
      const response = await axios.post(`dashboard/member/benefits/${benefit.id}/action`);
      const result = response?.data || {};

      if (result.type === 'LINK') {
        const targetUrl = result.url || benefit?.providerUrl;
        setStep("");
        if (!targetUrl) {
          pendingWindow?.close();
          return;
        }
        const absoluteUrl = toAbsoluteUrl(targetUrl);
        if (pendingWindow) {
          pendingWindow.location.href = absoluteUrl;
        } else {
          window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
        }
        return;
      }

      // EMAIL (כולל המקרה של בקשה חוזרת בתוך 30 דקות): מציגים את ההודעה שחזרה מהשרת
      pendingWindow?.close();
      setResultMessage(result.message || "פרטיך נשלחו בהצלחה לנותן ההטבה.");
      setStep("success");
    } catch (error) {
      pendingWindow?.close();
      const serverMessage = error?.response?.data?.message || error?.response?.data?.error;
      setResultMessage(serverMessage || DEFAULT_ERROR_MESSAGE);
      setStep("error");
    } finally {
      setLoading(false);
    }
  }

  const closePopups = () => {
    if (loading) return;
    setStep("");
  }

  return (
    <>
      <article
        dir="rtl"
        onClick={handleCardClick}
        className={`w-full h-full flex flex-col bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden ${canRedeem ? 'cursor-pointer active:scale-[0.99] transition' : ''}`}
      >
        {/* תמונה + שם נותן ההטבה */}
        <div className="relative">
          <img
            src={resolveImageUrl(benefit?.imageUrl) || benefitFallback}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = benefitFallback; }}
            alt={benefit?.name || ""}
            className="w-full"
          />
          {benefit?.providerName && (
            <span className="absolute bottom-1.5 right-1.5 flex items-center gap-1 bg-white/90 text-[#0d406b] text-[10px] font-semibold rounded-full px-2 py-0.5 shadow max-w-[90%] truncate">
              <MdStorefront size={12} className="shrink-0" />
              <span className="truncate">{benefit.providerName}</span>
            </span>
          )}
        </div>

        {/* גוף הכרטיס */}
        <div className="p-2 text-right flex flex-col flex-grow">
          <h3 className="text-sm font-bold text-[#0d406b] leading-snug">{benefit?.name}</h3>
          {benefit?.description && (
            <p className="mt-1 text-xs text-[#595959] leading-relaxed whitespace-pre-line">{benefit.description}</p>
          )}

          {/* תוקף */}
          {validityText && (
            <div className="mt-auto pt-2 border-t border-gray-100 flex items-center">
              <span className="flex items-center gap-1 text-[10px] text-gray-500">
                <MdOutlineAccessTime size={14} className="shrink-0" />
                {validityText}
              </span>
            </div>
          )}
        </div>
      </article>

      <BenefitConfirmPopup
        show={step === "confirm"}
        title={confirmContent.title}
        message={confirmContent.message}
        loading={loading}
        onConfirm={handleConfirm}
        onClose={closePopups}
      />

      <BenefitResultPopup
        show={step === "success"}
        success
        title="הבקשה נשלחה"
        message={resultMessage}
        onClose={closePopups}
      />

      <BenefitResultPopup
        show={step === "error"}
        success={false}
        title="מימוש ההטבה נכשל"
        message={resultMessage}
        onClose={closePopups}
      />

      {/* תהליך הצטרפות לדמי חבר, זהה לזה שבמארזים */}
      {(step === "PremiumPopup") && <PremiumPopup show={true} onClose={() => setStep("")}
        onNext={(ErrMess) => {
          setStep("duesSuccess")
          setErrMess(ErrMess)
          setUserDetails({ ...userDetails, activeDues: true });
        }}
        onError={(ErrMess) => {
          setStep("duesError")
          setErrMess(ErrMess)
        }} />}
      {(step === "duesSuccess") && <SuccessAnimation onNext={() => setStep("")} titlerror={errMess} />}
      {(step === "duesError") && <PaymentFailure onClose={() => setStep("")} error={errMess} titlerror={"התשלום נכשל"} />}
    </>
  )
}

export default BenefitCard
